import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import SharedInlineAlert from '../../components/SharedInlineAlert';
import ManualFlowChart from './ManualFlowChart';
import { ManualText, ROLE_CLASS, mediaUrl, plainText, routeMatches } from './manualShared';

const TABS = [
    { key: 'tugas', label: 'Tugas Saya', icon: 'bi-list-check' },
    { key: 'aliran', label: 'Carta Aliran', icon: 'bi-diagram-3' },
    { key: 'baharu', label: 'Apa Baharu', icon: 'bi-stars' },
];

// Role-based visual user manual. Content comes from GET /manual (signed-in only), built by tools/user-manual.
const UserManual = () => {
    const apiUrl = process.env.REACT_APP_API_URL;
    const location = useLocation();
    const navigate = useNavigate();
    const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
    const taskId = params.get('tugas') || '';
    const screen = params.get('skrin') || '';
    const tab = TABS.some((t) => t.key === params.get('tab')) ? params.get('tab') : 'tugas';
    const viewAs = params.get('sebagai') || '';

    const [manual, setManual] = useState(null);
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [query, setQuery] = useState('');
    const [zoom, setZoom] = useState(null);

    useEffect(() => {
        if (!apiUrl) return;
        const token = localStorage.getItem('token');
        const [as, pi] = viewAs.split(':');
        setIsLoading(true);
        axios.get(`${apiUrl}/manual`, {
            headers: token ? { Authorization: `Bearer ${token}` } : undefined,
            params: as ? { as, pi: pi === 'pi' ? 1 : 0 } : undefined,
        })
            .then((response) => { setManual(response?.data?.data || null); setError(''); })
            .catch((err) => setError(err?.response?.data?.message || 'Manual tidak dapat dimuatkan.'))
            .finally(() => setIsLoading(false));
    }, [apiUrl, viewAs]);

    const setParams = useCallback((next) => {
        const merged = new URLSearchParams(location.search);
        Object.entries(next).forEach(([k, v]) => { if (v) merged.set(k, v); else merged.delete(k); });
        navigate({ search: merged.toString() ? `?${merged}` : '' });
        window.scrollTo(0, 0);
    }, [location.search, navigate]);

    const openTask = useCallback((id) => setParams({ tugas: id, skrin: '', tab: '' }), [setParams]);

    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') setZoom(null); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, []);

    const tasks = useMemo(() => manual?.tasks || [], [manual]);
    const screenTasks = useMemo(() => (screen ? tasks.filter((t) => (t.routes || []).some((r) => routeMatches(r, screen))) : []), [screen, tasks]);

    // ? Bantuan with exactly one matching task goes straight to it.
    useEffect(() => {
        if (screen && !taskId && screenTasks.length === 1) {
            navigate({ search: `?tugas=${screenTasks[0].id}` }, { replace: true });
        }
    }, [screen, taskId, screenTasks, navigate]);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        const base = screen ? screenTasks : tasks;
        if (!q) return base;
        return base.filter((t) => `${t.title} ${t.summary} ${t.steps.map((s) => `${s.title} ${(s.marks || []).join(' ')}`).join(' ')}`.toLowerCase().includes(plainText(q)));
    }, [query, screen, screenTasks, tasks]);

    const task = tasks.find((t) => t.id === taskId);
    const taskIndex = task ? tasks.indexOf(task) : -1;

    const header = manual && (
        <div className="app-manual-hero">
            <div>
                <div className="app-section-eyebrow">MANUAL PENGGUNA</div>
                <h2>Panduan untuk {manual.role_label}</h2>
                <p>Tugas harian anda dengan gambar langkah demi langkah. Kotak merah bernombor dalam gambar ialah tempat untuk klik atau isi.</p>
                <div className="app-manual-chips">
                    <span className="app-manual-chip"><i className="bi bi-person-badge"></i> {manual.role_label}</span>
                    {manual.is_pi && <span className="app-manual-chip is-pi"><i className="bi bi-pencil-square"></i> Pegawai Inkuiri (PI)</span>}
                    <span className="app-manual-chip is-muted">Dikemas kini {manual.updated}</span>
                </div>
            </div>
            {manual.can_view_as && (
                <label className="app-manual-viewas" htmlFor="manual-view-as">
                    <span>Lihat sebagai</span>
                    <select id="manual-view-as" value={viewAs} onChange={(e) => setParams({ sebagai: e.target.value, tugas: '', skrin: '' })}>
                        <option value="">Peranan saya (semua tugas)</option>
                        {manual.view_as_options.map((o) => (
                            <React.Fragment key={o.role}>
                                <option value={o.role}>{o.label}</option>
                                {o.role === 'pegawai_hq' && <option value="pegawai_hq:pi">{o.label} + PI</option>}
                            </React.Fragment>
                        ))}
                    </select>
                </label>
            )}
        </div>
    );

    if (isLoading && !manual) {
        return <div className="app-section"><div className="app-empty">Memuatkan manual…</div></div>;
    }
    if (error) {
        return <div className="app-section"><SharedInlineAlert type="error" message={error} /></div>;
    }
    if (!manual) return null;

    // ---------- single task ----------
    if (task) {
        const prev = tasks[taskIndex - 1];
        const next = tasks[taskIndex + 1];
        return (
            <div className="app-section app-manual">
                <button type="button" className="app-back" onClick={() => setParams({ tugas: '' })}>
                    <i className="bi bi-arrow-left"></i> Semua tugas
                </button>
                <header className="app-manual-task-head">
                    <div className="app-section-eyebrow">{manual.role_label}{task.pi ? ' · PI' : ''}</div>
                    <h2>{task.title}</h2>
                    <p>{task.summary}</p>
                    <nav className="app-manual-step-index" aria-label="Langkah">
                        {task.steps.map((s, i) => (
                            <a key={i} href={`#langkah-${i + 1}`} onClick={(e) => { e.preventDefault(); document.getElementById(`langkah-${i + 1}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }}>
                                <b>{i + 1}</b>{plainText(s.title)}
                            </a>
                        ))}
                    </nav>
                </header>

                {task.video?.url && (
                    <section className="app-card app-manual-video">
                        <h4><i className="bi bi-play-circle"></i> Tonton dahulu</h4>
                        <video controls preload="none" playsInline poster={mediaUrl(task.video.poster_url)} src={mediaUrl(task.video.url)}>
                            Pelayar anda tidak dapat memainkan video ini.
                        </video>
                    </section>
                )}

                {task.steps.map((step, i) => (
                    <article key={i} id={`langkah-${i + 1}`} className="app-card app-manual-step">
                        <header className="app-manual-step-head">
                            <span className="app-manual-step-num">{i + 1}</span>
                            <div className="app-manual-step-title">
                                <span className={`app-manual-role ${ROLE_CLASS[step.role] || ''}`}>{manual.role_labels[step.role] || step.role}</span>
                                <h3><ManualText text={step.title} /></h3>
                                {step.menu && (
                                    <div className="app-manual-menu">
                                        <i className="bi bi-signpost-split"></i>
                                        {step.menu.split('›').map((p, k) => (
                                            <React.Fragment key={k}>{k > 0 && <b>›</b>}<span>{p.trim()}</span></React.Fragment>
                                        ))}
                                    </div>
                                )}
                            </div>
                            {step.status && <div className="app-manual-step-status"><small>Status selepas langkah</small><span>{step.status}</span></div>}
                        </header>
                        {step.marks?.length > 0 && (
                            <ol className="app-manual-marks">{step.marks.map((m, k) => <li key={k}><ManualText text={m} /></li>)}</ol>
                        )}
                        {step.notes?.length > 0 && (
                            <ul className="app-manual-notes">{step.notes.map((m, k) => <li key={k}><ManualText text={m} /></li>)}</ul>
                        )}
                        {step.images?.length > 0 && (
                            <div className={`app-manual-figs${step.pair ? ' is-pair' : ''}`}>
                                {step.images.map((img) => (
                                    <figure key={img.name} className={`app-manual-fig${img.narrow ? ' is-narrow' : ''}${img.small ? ' is-small' : ''}`}>
                                        <button type="button" onClick={() => setZoom(img)} aria-label="Besarkan gambar">
                                            <img src={mediaUrl(img.url)} alt={plainText(img.caption) || plainText(step.title)} width={img.width} height={img.height} loading="lazy" />
                                        </button>
                                        {img.caption && <figcaption><ManualText text={img.caption} /></figcaption>}
                                    </figure>
                                ))}
                            </div>
                        )}
                    </article>
                ))}

                {task.transcripts && manual.transcripts?.length > 0 && (
                    <section className="app-card">
                        <h4>Contoh perbualan sebenar</h4>
                        {manual.transcripts.map((p, i) => (
                            <details key={i} className="app-manual-transcript" open={i === 0}>
                                <summary><strong>{p.name}</strong> <small>{p.scenario}</small></summary>
                                <div className="app-manual-chat">
                                    {p.events.map((e, k) => (
                                        <div key={k} className={`app-manual-bubble ${e.who === 'user' ? 'is-me' : 'is-bot'}`}>
                                            {e.type === 'image' ? <em>🪪 {e.text}</em> : String(e.text).replace(/\*/g, '')}
                                            <time>{e.time}</time>
                                        </div>
                                    ))}
                                </div>
                            </details>
                        ))}
                    </section>
                )}

                <nav className="app-manual-pager">
                    {prev ? <button type="button" className="app-button app-button-ghost" onClick={() => openTask(prev.id)}><i className="bi bi-arrow-left"></i> {prev.title}</button> : <span />}
                    {next && <button type="button" className="app-button" onClick={() => openTask(next.id)}>{next.title} <i className="bi bi-arrow-right"></i></button>}
                </nav>
                {zoom && (
                    <div className="app-manual-lightbox" role="dialog" aria-label="Gambar besar" onClick={() => setZoom(null)}>
                        <img src={mediaUrl(zoom.url)} alt={plainText(zoom.caption)} />
                    </div>
                )}
            </div>
        );
    }

    // ---------- overview ----------
    return (
        <div className="app-section app-manual">
            {header}
            <div className="app-stepper app-manual-tabs">
                {TABS.map((t) => (
                    <button key={t.key} type="button" className={`app-step ${tab === t.key ? 'active' : ''}`} onClick={() => setParams({ tab: t.key === 'tugas' ? '' : t.key, skrin: '' })}>
                        <span><i className={`bi ${t.icon}`}></i> {t.label}</span>
                    </button>
                ))}
            </div>

            {tab === 'tugas' && (
                <div className="app-tab-panel">
                    {screen && (
                        <SharedInlineAlert type="info">
                            <div className="app-manual-screen-note">
                                <span>{screenTasks.length ? `Panduan untuk skrin ini (${screenTasks.length} tugas).` : 'Tiada panduan khusus untuk skrin ini. Berikut semua tugas anda.'}</span>
                                <button type="button" className="app-manual-link" onClick={() => setParams({ skrin: '' })}>Lihat semua tugas</button>
                            </div>
                        </SharedInlineAlert>
                    )}
                    <div className="app-manual-search">
                        <i className="bi bi-search"></i>
                        <input id="manual-search" type="search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Cari tugas, contoh: Muktamad, kes, waran" aria-label="Cari tugas" />
                    </div>
                    <div className="app-manual-grid">
                        {(screen && !screenTasks.length ? tasks : filtered).map((t) => (
                            <button key={t.id} type="button" className="app-manual-card" onClick={() => openTask(t.id)}>
                                <div className="app-manual-thumb">
                                    {(t.video?.poster_url || t.thumbnail_url) ? <img src={mediaUrl(t.video?.poster_url || t.thumbnail_url)} alt="" loading="lazy" /> : <i className="bi bi-whatsapp"></i>}
                                    {t.video?.url && <span className="app-manual-badge"><i className="bi bi-play-fill"></i> Video</span>}
                                    {t.pi && <span className="app-manual-badge is-pi">PI</span>}
                                </div>
                                <div className="app-manual-card-body">
                                    <strong>{t.title}</strong>
                                    <p>{t.summary}</p>
                                    <small>{t.steps.length} langkah</small>
                                </div>
                            </button>
                        ))}
                    </div>
                    {filtered.length === 0 && !(screen && !screenTasks.length) && <div className="app-empty">Tiada tugas sepadan dengan carian.</div>}
                </div>
            )}

            {tab === 'aliran' && (
                <div className="app-tab-panel">
                    {manual.flows.map((flow) => (
                        <ManualFlowChart key={flow.id} flow={flow} roleLabels={manual.role_labels} viewerRole={manual.role} onOpenTask={openTask} />
                    ))}
                </div>
            )}

            {tab === 'baharu' && (
                <div className="app-tab-panel">
                    <section className="app-card">
                        <h4>Perubahan sistem (URS 4 September 2026)</h4>
                        <ul className="app-manual-news">
                            {manual.whats_new.map((row, i) => (
                                <li key={i}>
                                    <span className="app-manual-news-no">Item {row.no}</span>
                                    <span><ManualText text={row.text} /></span>
                                    {row.task && <button type="button" className="app-manual-link" onClick={() => openTask(row.task)}>Panduan</button>}
                                </li>
                            ))}
                        </ul>
                    </section>
                </div>
            )}
        </div>
    );
};

export default UserManual;
